import React, { useEffect, useState, useCallback } from "react";
import get from "lodash/get";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { getApiPath, ADD_UPDATE } from "../../api/ApiConst";
import apiCaller from "../../api/ApiCaller";
import Select from 'react-select';
import textUtils from "../common/utilities/TextUtils";

/**
 * Function to create an initial state object for form based on document meta attributes
 * @param {Array} attributes 
 */
const getInitialStateObject = (attributes) => {
    const initialState = {};
    attributes.forEach((attribute) => {
        const fieldName = attribute.value;
        initialState[fieldName] = attribute.defaultValue ?? "";
        if (attribute.type === 'select' && !attribute.dataList) {
            attribute.dataList = attribute.dataListFunction?.() || [];
        }
    });
    return initialState;
};

/**
 * Function to map row data to form state
 * @param {Array} attributes 
 * @param {Object} row 
 */
const mapRowToFormState = (attributes, row) => {
    const initialState = {};
    attributes.forEach((attribute) => {
        const fieldName = attribute.value;
        initialState[fieldName] = get(row, fieldName, "");
    });
    return initialState;
};

const DocumentAddEdit = ({
    isOpen = false,
    row = null,
    onSuccessCallBack,
    onClose,
    documentMeta
}) => {
    const [addEditModel, setAddEditModel] = useState(() => getInitialStateObject(documentMeta.attributes));

    useEffect(() => {
        if (isOpen) {
            if (row?.id) {
                setAddEditModel(mapRowToFormState(documentMeta.attributes, row));
            } else {
                setAddEditModel(getInitialStateObject(documentMeta.attributes));
            }
        }
    }, [isOpen, row, documentMeta.attributes]);

    const toggle = useCallback(() => onClose(), [onClose]);

    const handleSelectChange = (selectedOption, field) => {
        setAddEditModel((prev) => ({
            ...prev,
            [field.value]: selectedOption?.value || ""
        }));
    };

    const handleCheckBoxChange = (checked, field) => {
        setAddEditModel((prev) => ({
            ...prev,
            [field]: checked
        }));
    };

    const handleTextChange = (key, value) => {
        setAddEditModel((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleCreateParameter = () => {
        let requestObj = { ...addEditModel };
    
        // Remove `id` field if it's empty or not defined
        if (!requestObj.id) {
            delete requestObj.id;
        }
    
        // Clean up empty fields from the request object
        for (const field in requestObj) {
            if (textUtils.isEmpty(requestObj[field], field, true)) {
                delete requestObj[field];
            }
        }
    
        const url = getApiPath(documentMeta.basePath, ADD_UPDATE);

        // Call API to save data
        apiCaller.callPostApi(
            url,
            requestObj,
            {}, // Headers if needed
            (response) => {
                toast.success(
                    `${documentMeta.label} ${addEditModel?.id ? "Updated" : "Created"} Successfully`
                );
                toggle(); // Close the modal
                onSuccessCallBack(); // Trigger the callback
            },
            (error) => {
                console.error("error", error);
                toast.error(
                    error.response?.data?.message || "An error occurred while saving"
                );
            }
        );
    };
    

    return (
        <Modal show={isOpen} onHide={toggle}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {`${addEditModel?.id ? "Edit" : "Add"} ${documentMeta.label}`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {documentMeta.attributes.map((field, index) => (
                    <div key={index} className="form-group row mb-3">
                        <div className="col-lg-12">
                            {field.formAttribute && <label htmlFor={field.defaultValue === undefined?field.value:field.defaultValue}>{field.name}</label>}

                            {(field.type === "text" || field.type === "number" ) && field.formAttribute && (
                                <input
                                    type={field.type}
                                    name={field.value}
                                    id={field.value}
                                    className={field.className || "form-control"}
                                    aria-label={field.name}
                                    onChange={(e) => handleTextChange(field.value, e.target.value)}
                                    value={addEditModel[field.value]}
                                />
                            )}

                            {field.type === "select" && (
                                <Select
                                    options={field.dataList}
                                    value={field.dataList.find(option => option.value === addEditModel[field.value])}
                                    onChange={(e) => {
                                        if (typeof field.handleSelectChange === "function") {
                                            field.handleSelectChange(e, field);
                                        } else {
                                            handleSelectChange(e, field);
                                        }
                                    }}
                                    id={field.value}
                                    aria-label={field.name}
                                    className="react-select-container"
                                />
                            )}

                            {field.type === "checkbox" && (
                                <input
                                    type="checkbox"
                                    name={field.value}
                                    id={field.value}
                                    checked={addEditModel[field.value]}
                                    onChange={(e) => handleCheckBoxChange(e.target.checked, field.value)}
                                    aria-label={field.name}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleCreateParameter}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DocumentAddEdit;
